import { Product, Variant } from "@/types/types.ts";
import { UnderlinedButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import styles from "./GalleryTeaser.module.scss";
import { formatAmplienceImageURL } from "@JohnLewisPartnership/jl-design-system/dist/utils/imageUrlModifier/imageUrlModifier";
import { useState } from "react";
import { ZoomModal } from "@/components/Galleries/components/ZoomModal/ZoomModal.tsx";
import { getGalleryObjects, VideoPreloadValue } from "../utils/utils.ts";
import { dispatchGalleryMonetateQEvent } from "../components/Analytics/dispatchGalleryMonetateQEvent";
import { ImageElementRef } from "@/components/Galleries/components/GalleryImageElement/ImageElement/ImageElement.tsx";

export interface GalleryTeaserUIProps {
  product: Product;
  variant: Variant;
  openModalOnClick: boolean;
  videoPreload: VideoPreloadValue;
  carouselGalleryInpTest?: boolean;
  ref?: React.RefObject<ImageElementRef>;
}

export const ImageHeight = 320;
export const ImageWidth = 240;

export const GalleryTeaserUI = ({
  variant,
  product,
  openModalOnClick,
  videoPreload,
  carouselGalleryInpTest = false,
}: GalleryTeaserUIProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const hasMultipleImages = !!variant.images.alternatives.length;

  const media = "media" in product ? product.media : null;
  const videoItems = media?.videos || [];

  const { images, videos } = getGalleryObjects(
    variant.title,
    variant.images,
    videoItems,
  );

  const handleScrollToGallery = () => {
    const carouselGalleryScrollEvent = new CustomEvent(
      "carousel-gallery-scroll-into-view",
    );
    document.dispatchEvent(carouselGalleryScrollEvent);
  };

  const handleOpenModal = () => {
    dispatchGalleryMonetateQEvent(
      "ZoomIcon",
      undefined,
      undefined,
      carouselGalleryInpTest,
    );
    setIsOpen(true);
  };

  const handleViewImagesButtonClick = openModalOnClick
    ? handleOpenModal
    : handleScrollToGallery;

  return (
    <div
      data-testid="gallery-teaser"
      className={styles.wrapper}
      data-component="GalleryTeaser"
    >
      <ZoomModal
        closeZoomModal={() => setIsOpen(false)}
        mediaItems={[...images, ...videos]}
        setSelectedIndex={setSelectedIndex}
        selectedIndex={selectedIndex}
        videoPreload={videoPreload}
        carouselGalleryInpTest={carouselGalleryInpTest}
        isZoomModalOpen={isOpen}
      />
      <button
        type="button"
        className={styles.mainImageButton}
        onClick={handleViewImagesButtonClick}
        aria-label="main product image"
        data-testid="gallery-teaser:main-image:button"
      >
        <img
          src={formatAmplienceImageURL(
            `${variant.images.primary}?wid=${ImageWidth}&hei=${ImageHeight}`,
          )}
          alt={variant.title}
          className={styles.mainImage}
        />
      </button>
      <UnderlinedButton
        onClick={handleViewImagesButtonClick}
        className={styles.viewMore}
      >
        {hasMultipleImages ? "View images" : "View image"}
      </UnderlinedButton>
    </div>
  );
};
